import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'antd';

interface BottomSheetProps {
  /** Called on backdrop tap and Escape. */
  onClose: () => void;
  /** Panel styles (background, layout); position, size and corners are set here. */
  style?: React.CSSProperties;
  className?: string;
  /** Element or component rendered as the panel, e.g. an antd Card; defaults to a div. */
  as?: React.ElementType;
  children: React.ReactNode;
  /** Extra props for the `as` element (e.g. Card `styles`). */
  panelProps?: Record<string, unknown>;
}

/**
 * Small-screen panel docked to the bottom edge, used instead of anchored popovers that do not fit on
 * a phone: full width, up to 85% of the viewport tall, over a dimmed backdrop. Tapping the backdrop or
 * Escape closes it, and the page behind does not scroll. Rendered in a portal so transformed ancestors
 * (page transitions) cannot offset its fixed position. Render it only while open.
 */
export function BottomSheet({ onClose, style, className, as: Panel = 'div', panelProps, children }: BottomSheetProps) {
  const { token } = theme.useToken();
  const zIndex = (token.zIndexPopupBase ?? 1000) + 20;

  // Close on Escape, and keep the page behind the sheet from scrolling.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <>
      <div
        aria-hidden
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: zIndex - 1, background: token.colorBgMask }}
      />
      <Panel
        role="dialog"
        className={className}
        {...panelProps}
        style={{
          ...style,
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex,
          width: '100%',
          maxHeight: '85dvh',
          borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0`,
          boxShadow: token.boxShadowSecondary,
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}
      >
        {children}
      </Panel>
    </>,
    document.body
  );
}
